import React, { useState, useEffect } from 'react';
import { obtenerHistorialCreditosAdmin } from '../../services/contenido';
import { listarUsuarios } from '../../services/usuario';
import '../../styles/admin/AdminConsumo.css';

function AdminConsumo() {
  const [historial, setHistorial] = useState([]);
  const [totalCreditos, setTotalCreditos] = useState(0);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filtros
  const [usuarioId, setUsuarioId] = useState('');
  const [emailBusqueda, setEmailBusqueda] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  // Cargar lista de usuarios para el selector
  useEffect(() => {
    const cargarUsuarios = async () => {
      try {
        const data = await listarUsuarios(0, 1000);
        setUsuarios(data);
      } catch (err) {
        console.error('Error cargando usuarios:', err);
      }
    };
    cargarUsuarios();
  }, []);

  const cargarHistorial = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        skip: skip,
        limit: limit
      };
      
      if (usuarioId) params.usuarioId = parseInt(usuarioId);
      if (emailBusqueda.trim()) params.email = emailBusqueda.trim();
      if (fechaDesde) params.fechaDesde = fechaDesde;
      if (fechaHasta) params.fechaHasta = fechaHasta;

      const data = await obtenerHistorialCreditosAdmin(params);
      setHistorial(data.historial || []);
      setTotalCreditos(data.total_creditos_consumidos || 0);
      setTotalRegistros(data.total || 0);
    } catch (err) {
      setError('Error al cargar el historial: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Cargar al cambiar filtros o paginación
  useEffect(() => {
    cargarHistorial();
  }, [usuarioId, emailBusqueda, fechaDesde, fechaHasta, skip, limit]);

  const handleBuscar = () => {
    setSkip(0);
    cargarHistorial();
  };

  const handleLimpiarFiltros = () => {
    setUsuarioId('');
    setEmailBusqueda('');
    setFechaDesde('');
    setFechaHasta('');
    setSkip(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-consumo-container">
      <div className="admin-consumo-header">
        <h1>📈 Historial de Consumo de Créditos</h1>
        <p>Consulta el detalle de créditos gastados por usuario</p>
      </div>

      {/* FILTROS */}
      <div className="admin-consumo-filtros">
        <div className="filtro-group">
          <label>Usuario</label>
          <select
            value={usuarioId}
            onChange={(e) => setUsuarioId(e.target.value)}
          >
            <option value="">Todos los usuarios</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre} 
              </option>
            ))}
          </select>
        </div>

        <div className="filtro-group">
          <label>Buscar por email</label>
          <input
            type="text"
            placeholder="Email del usuario..."
            value={emailBusqueda}
            onChange={(e) => setEmailBusqueda(e.target.value)}
          />
        </div>

        <div className="filtro-group">
          <label>Fecha desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
          />
        </div>

        <div className="filtro-group">
          <label>Fecha hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
          />
        </div>

        <div className="filtro-actions">
          <button className="btn-buscar" onClick={handleBuscar}>
            🔍 Buscar
          </button>
          <button className="btn-limpiar" onClick={handleLimpiarFiltros}>
            Limpiar
          </button>
        </div>
      </div>

      {/* RESUMEN */}
      <div className="admin-consumo-resumen">
        <div className="resumen-card">
          <span className="resumen-label">Total de créditos gastados</span>
          <span className="resumen-valor">{totalCreditos}</span>
        </div>
        <div className="resumen-card">
          <span className="resumen-label">Registros encontrados</span>
          <span className="resumen-valor">{totalRegistros}</span>
        </div>
      </div>

      {/* TABLA */}
      <div className="admin-consumo-table-wrapper">
        {loading ? (
          <div className="admin-consumo-loading">Cargando historial...</div>
        ) : error ? (
          <div className="admin-consumo-error">
            <p>{error}</p>
            <button onClick={cargarHistorial}>Reintentar</button>
          </div>
        ) : historial.length === 0 ? (
          <div className="admin-consumo-empty">
            <p>No hay registros de consumo para los filtros seleccionados.</p>
          </div>
        ) : (
          <>
            <table className="admin-consumo-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Campaña</th>
                  <th>Tipo</th>
                  <th>Créditos</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.fecha)}</td>
                    <td>{item.usuario_nombre || 'N/A'}</td>
                    <td>{item.usuario_email || 'N/A'}</td>
                    <td>{item.campania_nombre || 'N/A'}</td>
                    <td>
                      <span className="tipo-badge">
                        {item.tipo === 'video' ? '🎬 Video' : '🖼️ Imagen'}
                      </span>
                    </td>
                    <td>
                      <span className="creditos-badge">
                        {item.creditos_gastados}
                      </span>
                    </td>
                    <td>
                      <span className={`estado-badge ${item.expirado ? 'expirado' : 'activo'}`}>
                        {item.expirado ? '⏳ Expirado' : '✅ Disponible'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* PAGINACIÓN */}
            <div className="admin-consumo-paginacion">
              <span>
                Mostrando {skip + 1} - {Math.min(skip + limit, totalRegistros)} de {totalRegistros}
              </span>
              <div className="paginacion-buttons">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                >
                  ← Anterior
                </button>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= totalRegistros}
                >
                  Siguiente →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminConsumo;